import React, { useEffect, useState } from "react";
import { useDisclosure } from "@jumbo/components/JumboDialog/hooks/useDisclosure";
import DialogDefault from "@jumbo/components/JumboDialog/components/DialogDefault";
import DialogConfirm from "@jumbo/components/JumboDialog/components/DialogConfirm";
import moment from "moment";

import Stack from "@mui/material/Stack";
import {
  Button,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { Card, CardContent } from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import ClearIcon from "@mui/icons-material/Clear";
import RefreshIcon from "@mui/icons-material/Refresh";

import { usePagination } from "app/hooks/usePagination";
import { Table } from "app/components";
import AddCollection from "./add-collection/index";
import Div from "@jumbo/shared/Div";
import {
  createCollection,
  deleteCollection,
  getCollectionList,
  toggleCollectionStatus,
  updateCollection,
} from "app/redux/slice/collectionSlice";
import { useDispatch, useSelector } from "react-redux";
import {
  markModuleRead,
  fetchUnreadCount,
} from "app/redux/slice/notificationSlice";
import { useSnackbar } from "notistack";
import Loader from "@jumbo/components/Loader";
import { userType } from "app/helpers/userType";

const Collection = () => {
  const { isOpen, onClose, onOpen, stageAction, actionProps } =
    useDisclosure();
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const { isMA } = userType();

  const collectionData = useSelector((state) => state?.collection);
  const [searchText, setSearchText] = useState("");
  const [status, setStatus] = useState("All");
  const [order, setOrder] = useState("desc");
  const [orderBy, setOrderBy] = useState("createdAt");

  const {
    isOpen: isDelete,
    onClose: closeDelete,
    actionProps: deleteActionProps,
    stageAction: deleteStageAction,
  } = useDisclosure();
  const { page, rowsPerPage, handleChangePage, handleChangeRowsPerPage } =
    usePagination({ id: "collection" });
  const [permissionData] = useState(
    JSON.parse(localStorage.getItem("permission")),
  );

  const [permission, setPermission] = useState({
    createPermission: false,
    updatePermission: false,
    deletePermission: false,
  });
  const getPermission = () => {
    if (isMA) {
      setPermission({
        createPermission: true,
        updatePermission: true,
        deletePermission: true,
      });
    } else {
      let permissionValue = {
        createPermission: false,
        updatePermission: false,
        deletePermission: false,
      };
      const rolePermission = permissionData?.filter((item) => {
        return item?.moduleName?.includes("Collection") && item;
      });
      rolePermission?.length &&
        rolePermission.forEach(function (num) {
          if (num?.name === "create-collection") {
            permissionValue.createPermission = true;
          }
          if (num?.name === "delete-collection") {
            permissionValue.deletePermission = true;
          }
          if (num?.name === "update-collection") {
            permissionValue.updatePermission = true;
          }
        });
      setPermission(permissionValue);
    }
  };
  useEffect(() => {
    getPermission();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleToggleStatus = async (row) => {
    const res = await dispatch(
      toggleCollectionStatus({ id: row._id, status: !row.status }),
    ).unwrap();
    if (res?.status === 200) {
      getData();
    } else {
      enqueueSnackbar(res?.data?.responseMessage || "Update failed", {
        variant: "error",
      });
    }
  };

  const columns = [
    { id: "name", label: "Name", sort: true },
    { id: "description", label: "Description", sort: false },
    {
      id: "status",
      label: "Status",
      align: "center",
      minWidth: 100,
      render: (row) => (
        <Tooltip title={row.status !== false ? "Deactivate" : "Activate"}>
          <Switch
            checked={row.status !== false}
            disabled={!permission.updatePermission}
            onChange={() => handleToggleStatus(row)}
            color="success"
            size="small"
          />
        </Tooltip>
      ),
    },
    {
      id: "createdAt",
      label: "Created Date",
      sort: true,
      render: (row) => moment(row?.createdAt).format("DD-MM-YYYY"),
    },
    {
      id: "actions",
      label: "Actions",
      minWidth: 100,
      align: "right",
      render: (row) => (
        <>
          {permission.updatePermission && (
            <IconButton
              onClick={() => {
                stageAction(row);
              }}
            >
              <EditIcon />
            </IconButton>
          )}
          {permission.deletePermission && (
            <IconButton
              onClick={() => {
                deleteStageAction(row);
              }}
            >
              <DeleteIcon color={"error"} />
            </IconButton>
          )}
        </>
      ),
    },
  ];

  const handleSubmit = async (data) => {
    const newCollectionData = {
      name: data.name?.trim(),
      description: data.description?.trim(),
    };
    const response = await dispatch(
      actionProps?._id
        ? updateCollection({ id: actionProps._id, data: newCollectionData })
        : createCollection(newCollectionData),
    ).unwrap();
    if (response.status === 200) {
      enqueueSnackbar(response.data?.responseMessage, {
        variant: "success",
      });
      getData();
      onClose();
    } else {
      enqueueSnackbar(response.data?.responseMessage, {
        variant: "error",
      });
    }
  };

  const DeleteCollection = () => {
    dispatch(deleteCollection(deleteActionProps?._id))
      .unwrap()
      .then((res) => {
        if (res?.status === 200) {
          enqueueSnackbar(res.data?.responseMessage, {
            variant: "success",
          });
          getData();
          closeDelete();
        } else {
          enqueueSnackbar(res.data?.responseMessage, {
            variant: "error",
          });
          closeDelete();
        }
      });
  };

  const getData = () => {
    localStorage.setItem("collection-search", searchText.trim());
    const value = {
      page: page,
      perPage: rowsPerPage,
      search: searchText.trim(),
      ...(status !== "All" && { status: status === "Active" }),
    };
    if (orderBy !== "") {
      value.sort = order === "asc" ? 1 : -1;
      value.field = orderBy;
    }
    dispatch(getCollectionList(value));
    dispatch(markModuleRead("Collection")).then(() => {
      dispatch(fetchUnreadCount("Collection"));
    });
  };

  useEffect(() => {
    getData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, dispatch, order, orderBy]);

  const handleKeyPress = (event) => {
    if (event.key === "Enter") {
      handleChangePage(0, 0);
      getData();
    }
  };

  const sortData = (Order, OrderBy) => {
    setOrder(Order);
    setOrderBy(OrderBy);
  };

  return (
    <>
      {collectionData.loading ? (
        <Loader />
      ) : (
        <>
          <Card sx={{ display: "flex", mb: 3 }}>
            <Div sx={{ display: "flex", flexDirection: "column", flex: "1" }}>
              <CardContent>
                <Grid container spacing={{ xs: 2, md: 3 }}>
                  <Grid item xs={12} sm={7}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Search"
                      value={searchText}
                      onChange={(e) => setSearchText(e.target.value)}
                      onKeyPress={handleKeyPress}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment
                            position="end"
                            style={{
                              display: searchText === "" ? "none" : "flex",
                            }}
                            onClick={() => setSearchText("")}
                          >
                            <ClearIcon />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Status</InputLabel>
                      <Select
                        value={status}
                        label="Status"
                        onChange={(e) => setStatus(e.target.value)}
                      >
                        <MenuItem value={"All"}>All</MenuItem>
                        <MenuItem value={"Active"}>Active</MenuItem>
                        <MenuItem value={"Inactive"}>Inactive</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={2}>
                    <Button
                      fullWidth
                      type="submit"
                      variant="contained"
                      onClick={() => {
                        handleChangePage(0, 0);
                        getData();
                      }}
                    >
                      Submit
                    </Button>
                  </Grid>
                </Grid>
              </CardContent>
            </Div>
          </Card>
          <Card>
            <CardContent>
              <Stack
                justifyContent="space-between"
                direction={{ xs: "column", sm: "row" }}
                sx={{ mb: 3 }}
              >
                <Typography variant={"h2"} mb={0}>
                  Collections List
                </Typography>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Tooltip title="Refresh">
                    <IconButton
                      onClick={() => {
                        getData();
                      }}
                    >
                      <RefreshIcon />
                    </IconButton>
                  </Tooltip>
                  {permission.createPermission && (
                    <LoadingButton
                      onClick={() => {
                        onOpen();
                      }}
                      sx={{
                        mt: { xs: 1, md: 0 },
                      }}
                      type="submit"
                      variant="contained"
                    >
                      Add Collection
                    </LoadingButton>
                  )}
                </Stack>
              </Stack>
              <Table
                columns={columns}
                rows={collectionData.list}
                page={page}
                rowsPerPage={rowsPerPage}
                Order={order}
                count={collectionData.total}
                OrderBy={orderBy}
                sortData={sortData}
                handleChangePage={handleChangePage}
                handleChangeRowsPerPage={handleChangeRowsPerPage}
                editAction={stageAction}
              />
            </CardContent>
          </Card>
          <DialogDefault
            open={isOpen}
            onClose={onClose}
            content={
              <AddCollection
                value={actionProps}
                onClose={onClose}
                handleSubmit={handleSubmit}
              />
            }
            title={actionProps?._id ? "Update Collection" : "Add Collection"}
            fullWidth={true}
            maxWidth={"sm"}
            actions={<></>}
          />
          <DialogConfirm
            isOpen={isDelete}
            onClose={closeDelete}
            title="Are you sure you want to delete this Collection?"
            onConfirm={DeleteCollection}
            btnLabels={{ cancel: "Cancel", confirm: "Delete" }}
          />
        </>
      )}
    </>
  );
};

export default Collection;
